import { lazy, Suspense } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { Database } from 'lucide-react'
import { fetchDbSample, fetchDataSources } from '../../lib/api'
import { Page } from '../../components/layout/AppShell'
import { PageHeader } from '../../components/layout/PageHeader'
import { IconBadge, SectionLabel, Spinner, ErrorState, Grid, StatCard } from '../../components/ui'

const ResultGrid = lazy(() => import('../../components/ui/ResultGrid'))

export function DataSourceDetailPage() {
  const { name } = useParams({ from: '/app/data/$name' })
  const navigate = useNavigate()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['db-sample', name],
    queryFn: () => fetchDbSample(name),
  })
  const { data: sources } = useQuery({ queryKey: ['data-sources'], queryFn: fetchDataSources })
  const meta = sources?.find((s) => s.name === name)

  const rows = data?.rows ?? []
  const columns = rows.length > 0 ? Object.keys(rows[0]) : []

  return (
    <Page
      width="narrow"
      header={<PageHeader crumbs={[{ label: 'Data Lake', onClick: () => navigate({ to: '/data' }) }, { label: name }]} />}
    >
      <div className="flex items-center gap-3">
        <IconBadge color="indigo" lg>
          <Database size={20} />
        </IconBadge>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">{name}</h1>
          <p className="text-sm text-gray-500">Reference market dataset</p>
        </div>
      </div>

      {meta && (
        <Grid cols={3}>
          <StatCard label="Rows" value={meta.row_count != null ? meta.row_count.toLocaleString() : '—'} />
          <StatCard label="Table" value={<span className="font-mono text-sm">{meta.table}</span>} />
          <StatCard label="Bitemporal" value={meta.bitemporal ? 'yes' : 'no'} />
        </Grid>
      )}

      <div>
        <SectionLabel>Preview {data ? `· ${data.n_rows} rows` : ''}</SectionLabel>
        {isLoading ? (
          <Spinner />
        ) : isError ? (
          <ErrorState />
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">No rows to preview.</p>
        ) : (
          <Suspense fallback={<Spinner label="Loading preview…" />}>
            <ResultGrid result={{ title: name, columns, rows }} />
          </Suspense>
        )}
      </div>
    </Page>
  )
}
